import type { RootState } from '@/rootReducer';
import { useEffect, useState, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
	Page,
	Text,
	View,
	Document,
	Image,
	StyleSheet,
	PDFViewer,
} from '@react-pdf/renderer';
import ReactDOM from 'react-dom';

import {
	dashboardGetGooglePlaces,
	dashboardClearGooglePlaces
} from '@/modules/dashboard/dashboardActions';

import {
	demandReportGetDemandReport,
	demandReportGetDemandReports,
	demandReportResetDemandReportsFailure
} from '@/modules/demandReports/demandReportsActions';

import {
	ChevronColorless,
	DemandReportIcon,
	SearchIcon,
	CloseThin
} from '@/assets/images';

import { sendAnalytics } from '@/services/analyticsService';

import './styles.scss';
import Loader, { Status } from '@/components/Loader/Loader';
import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';

export default function DemandReports(): JSX.Element {
	const [
		user,
		demandReportData,
		demandReportAllReports,
		noReports,
		createDemandReportFailure,
		googlePlaces,
		googlePlacesFailure,
		googlePlacesLoading
	] = useSelector((state: RootState) => [
		state.user.getIn(['authorize', 'user']).toJS(),
		state.demandReports.getIn(['demandReportData']),
		state.demandReports.getIn(['demandReportAllReports']),
		state.demandReports.getIn(['demandReportAllReportsNoReports']),
		state.demandReports.getIn(['demandReportDataFailure']),
		state.dashboard.getIn(['googlePlaces', 'data']).toJS(),
		state.dashboard.getIn(['googlePlaces', 'failure']),
		state.dashboard.getIn(['googlePlaces', 'loading'])
	]);

	const provinces = [
		['Albertia', 'AB'],
		['British Columbia', 'BC'],
		['Manitoba', 'MB'],
		['New Brunswick', 'NB'],
		['Newfoundland', 'NF'],
		['Labrador', 'LB'],
		['Newfoundland and Labrador', 'NL'],
		['Northwest Territories', 'NT'],
		['Nova Scotia', 'NS'],
		['Nunavut', 'NU'],
		['Ontario', 'ON'],
		['Prince Edward Island', 'PE'],
		['Quebec', 'QC'],
		['Saskatchewan', 'SK'],
		['Yukon', 'YT']
	];

	const states = [
		['Arizona', 'AZ'],
		['Alabama', 'AL'],
		['Alaska', 'AK'],
		['Arkansas', 'AR'],
		['California', 'CA'],
		['Colorado', 'CO'],
		['Connecticut', 'CT'],
		['Washington DC', 'DC'],
		['Delaware', 'DE'],
		['Florida', 'FL'],
		['Georgia', 'GA'],
		['Hawaii', 'HI'],
		['Idaho', 'ID'],
		['Illinois', 'IL'],
		['Indiana', 'IN'],
		['Iowa', 'IA'],
		['Kansas', 'KS'],
		['Kentucky', 'KY'],
		['Louisiana', 'LA'],
		['Maine', 'ME'],
		['Maryland', 'MD'],
		['Massachusetts', 'MA'],
		['Michigan', 'MI'],
		['Minnesota', 'MN'],
		['Mississippi', 'MS'],
		['Missouri', 'MO'],
		['Montana', 'MT'],
		['Nebraska', 'NE'],
		['Nevada', 'NV'],
		['New Hampshire', 'NH'],
		['New Jersey', 'NJ'],
		['New Mexico', 'NM'],
		['New York', 'NY'],
		['North Carolina', 'NC'],
		['North Dakota', 'ND'],
		['Ohio', 'OH'],
		['Oklahoma', 'OK'],
		['Oregon', 'OR'],
		['Pennsylvania', 'PA'],
		['Rhode Island', 'RI'],
		['South Carolina', 'SC'],
		['South Dakota', 'SD'],
		['Tennessee', 'TN'],
		['Texas', 'TX'],
		['Utah', 'UT'],
		['Vermont', 'VT'],
		['Virginia', 'VA'],
		['Washington', 'WA'],
		['West Virginia', 'WV'],
		['Wisconsin', 'WI'],
		['Wyoming', 'WY']
	];

	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December'
	];

	const getGooglePlaces = (searchString: string) =>
		dispatch(dashboardGetGooglePlaces({ searchString }));
	const clearGooglePlaces = () => {
		dispatch(dashboardClearGooglePlaces());
	};

	const [locations] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['locations', 'data']).toJS()
		],
		shallowEqual
	);
	const addressRef = useRef<HTMLInputElement>(null);
	const [typingAddress, setTypingAddress] = useState(false);
	const [selectedAddress, setSelectedAddress] = useState('');
	let selectedAddressNonState;
	const [selectedZip, setSelectedZip] = useState('');
	const [selectedTown, setSelectedTown] = useState('');
	let selectedTownNonState;
	const [selectedState, setSelectedState] = useState('');
	const [selectedCountry, setSelectedCountry] = useState('');
	const [demandReportsAll, setDemandReportsAll] = useState({});
	const [searchLoading, setSearchLoading] = useState(false);
	const [reportLimitLeft, setReportLimitLeft] = useState();
	const [nationBubbleInd, setNationBubbleInd] = useState();
	const [nationBubbleInd2, setNationBubbleInd2] = useState();
	let demandReportFin;
	let topFive = [];
	let currentMonth;
	let currentYear;
	let nationBubbleIndNoState;
	let nationBubbleInd2NoState;

	const dispatch = useDispatch();

	const resetDemandReportsFailure = () =>
		dispatch(demandReportResetDemandReportsFailure());

	let report: any;

	const areWeTyping = e => {
		if (addressRef?.current?.contains(e.target)) {
			setTypingAddress(true);
			const handle = setTimeout(() => {
				setShowEmptyState(true);
				setTypingAddress(false);
				clearTimeout(handle);
			}, 5000);
		}
	};

	useEffect(() => {
		window.addEventListener('keyup', areWeTyping);

		return () => window.removeEventListener('keyup', areWeTyping);
	}, []);

	useEffect(() => {
		if (createDemandReportFailure) {
			setSearchLoading(false);
		}
	}, [createDemandReportFailure]);

	useEffect(() => {
		if (user && user.email) {
			setReportLimitLeft(user.demandReportsLimitLeft);
		}
	}, [user]);

	const getDemandReport = (payload: any) =>
		dispatch(demandReportGetDemandReport(payload));
	const getDemandReports = () => dispatch(demandReportGetDemandReports());

	useEffect(() => {
		getDemandReports();
		if (window.location.search.includes('open')) {
			openDemandReports();
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName:
						'User reached Demand Reports page from a location link',
					properties: {}
				});
			}
		}
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User reached Demand Reports page',
				properties: {}
			});
		}
	}, []);

	useEffect(() => {
		if (demandReportAllReports?.demandReports) {
			setDemandReportsAll(demandReportAllReports.demandReports);
			setReportLimitLeft(demandReportAllReports.demandLimitLeft);
			if (window.location.search.includes('drid')) {
				const drid = window.location.search.split('drid=')[1];
				let reportNew;
				for (
					let i = 0;
					i < demandReportAllReports.demandReports.length;
					i++
				) {
					if (demandReportAllReports.demandReports[i].id === drid) {
						reportNew = demandReportAllReports.demandReports[i];
						createPDFAddress(reportNew);
						currentMonth = reportNew.name.split(' ')[0];
						currentYear = reportNew.name.split(',')[1].trim();
						report = modelReport(reportNew.data);
						if (user && user.email) {
							sendAnalytics({
								type: 'track',
								userId: user.email,
								eventName:
									'User opened an existing demand report',
								properties: {
									deeplinked: true
								}
							});
						}
						setTimeout(() => {
							renderPdf();
						}, 1500);
						return;
					}
				}
			}

			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User got all previous demand reports',
					properties: {}
				});
			}
		}
	}, [demandReportAllReports]);

	const searchDemandReports = (evt: any) => {
		const searchStr = evt.currentTarget.value;
		if (searchStr.length > 0) {
			const newList = [];
			for (let i = 0; i < demandReportsAll.length; i++) {
				const isMatch = demandReportsAll[i].name.includes(searchStr);
				if (isMatch) {
					newList.push(demandReportsAll[i]);
				}
			}
			setDemandReportsAll(newList);
		} else {
			setDemandReportsAll(demandReportAllReports.demandReports);
		}
	};

	const addPercentagesToReport = (
		reportObj,
		svObject,
		nationalObject,
		businessCount
	) => {
		const reportArr = Object.keys(reportObj);
		const reportArrSV = Object.keys(svObject);
		const reportArrNational = Object.keys(nationalObject);

		let total = 0;
		reportArr.forEach(cat => {
			if (reportObj[cat] && reportObj[cat] != 'null') {
				total += reportObj[cat];
			}
		});

		let totalSV = 0;
		reportArrSV.forEach(cat => {
			if (svObject[cat] && svObject[cat] != 'null') {
				totalSV += svObject[cat];
			}
		});

		let totalNational = 0;
		reportArrNational.forEach(cat => {
			if (nationalObject[cat] && nationalObject[cat] != 'null') {
				totalNational += nationalObject[cat];
			}
		});

		const newReportObj = {};

		for (let i = 0; i < reportArr.length; i++) {
			const percentage = (reportObj[reportArr[i]] / total) * 100;

			const percentageSV = (svObject[reportArr[i]] / totalSV) * 100;
			const percentageDiffSV = percentage - percentageSV;

			const percentageNational =
				(nationalObject[reportArr[i]] / totalNational) * 100;
			const percentageDiffNational = percentage - percentageNational;

			const businessCountItem = businessCount[reportArr[i]];
			newReportObj[reportArr[i]] = {
				percentage,
				percentageSV,
				percentageNational,
				businessCountItem,
				percentageDiffSV,
				percentageDiffNational,
				value: reportObj[reportArr[i]]
			};
		}
		return newReportObj;
	};

	const replaceNulls = reportObj => {
		const updatedReportObj = {};
		Object.keys(reportObj).forEach(item => {
			if (reportObj[item]) {
				updatedReportObj[item] = reportObj[item];
			} else {
				updatedReportObj[item] = 0;
			}
		});

		return updatedReportObj;
	};

	const setNationBubbleIndFunc = reportsList => {
		for (let i = 5; i < reportsList.length; i++) {
			const key = Object.keys(reportsList[i])[0];

			if (reportsList[i][key]?.percentageDiffNational > 0) {
				setNationBubbleInd(i);
				nationBubbleIndNoState = i;
				return;
			}
		}
	};

	const modelReport = (report: any) => {
		demandReportFin = report;
		const { demandReports } = report;
		const { citySearchVolumes } = report;
		const { nationalSearchVolumes } = report;
		const { businessCount } = report;

		let updatedReport: any = replaceNulls(demandReports);
		updatedReport = addPercentagesToReport(
			updatedReport,
			citySearchVolumes,
			nationalSearchVolumes,
			businessCount
		);

		const sortedDemandReportsArr = Object.keys(updatedReport).sort(
			(a, b) => {
				if (
					updatedReport[a].percentageDiffSV &&
					!isNaN(updatedReport[a].percentageDiffSV) &&
					updatedReport[a].percentageDiffSV != 'null'
				) {
					if (
						updatedReport[a].percentageDiffSV >
						updatedReport[b].percentageDiffSV
					) {
						return -1;
					}
					if (
						updatedReport[a].percentageDiffSV <
						updatedReport[b].percentageDiffSV
					) {
						return 1;
					}
					return 0;
				}
				if (
					updatedReport[a].percentageDiffNational >
					updatedReport[b].percentageDiffNational
				) {
					return -1;
				}
				if (
					updatedReport[a].percentageDiffNational <
					updatedReport[b].percentageDiffNational
				) {
					return 1;
				}
				return 0;
			}
		);

		const sortedDemandReportsFinal = [];

		for (let i = 0; i < sortedDemandReportsArr.length; i++) {
			if (sortedDemandReportsArr[i]) {
				sortedDemandReportsFinal.push({
					[sortedDemandReportsArr[i]]:
						updatedReport[sortedDemandReportsArr[i]]
				});
			}
		}
		setTopFiveCategories(
			JSON.parse(JSON.stringify(sortedDemandReportsFinal))
		);
		setNationBubbleIndFunc(sortedDemandReportsFinal);
		return sortedDemandReportsFinal;
	};

	const setTopFiveCategories = list => {
		const sortedList = list.sort((a, b) => {
			const keya = Object.keys(a)[0];
			const keyb = Object.keys(b)[0];
			return b[keyb].percentage - a[keya].percentage;
		});

		const topFiveList = [];
		let otherPercent = 100;
		for (let i = 0; i < 4; i++) {
			const { percentage } = sortedList[i][Object.keys(sortedList[i])[0]];
			otherPercent -= percentage;
			topFiveList.push(sortedList[i]);
		}
		topFiveList.push({
			other: {
				percentage: otherPercent
			}
		});

		topFive = topFiveList;
	};

	const startDownload = () => {
		resetDemandReportsFailure();
		setSearchLoading(true);
		if (selectedState === 'Washington DC') {
			getDemandReport({
				zipCode: selectedZip,
				city: `Washington,District of Columbia,United States`
			});
		} else {
			getDemandReport({
				zipCode: selectedZip,
				city: `${selectedTown},${selectedState},${selectedCountry}`
			});
		}
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User Searched for a new Demand Report',
				properties: {}
			});
		}
	};

	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [showEmptyState, setShowEmptyState] = useState(false);

	useEffect(() => {
		if (
			demandReportData &&
			Object.keys(demandReportData).length > 0 &&
			!!demandReportData.demandReports &&
			(selectedAddress || selectedAddressNonState)
		) {
			report = modelReport(demandReportData);
			currentMonth = months[new Date().getMonth()];
			currentYear = parseInt(new Date().getYear()) + 1900;
			setTimeout(() => {
				renderPdf();
				setSearchLoading(false);
				getDemandReports();
			}, 500);
		}
	}, [demandReportData]);

	useEffect(() => {
		if (
			demandReportData &&
			demandReportData === 'Operation is successfully executed'
		) {
			setShowSuccessModal(true);
		}
	}, [demandReportData]);

	const renderPdf = () => {
		const PDF = () => (
			<PDFViewer>
				<DemandReportPDF />
			</PDFViewer>
		);
		document
			.getElementsByClassName('demand-reports-modal-pdf-viewer-outer')[0]
			.classList.add('exist-demand-reports');
		ReactDOM.render(
			<PDF />,
			document.getElementsByClassName(
				'demand-reports-modal-pdf-viewer'
			)[0]
		);
	};

	const closePDFPreview = () => {
		document
			.getElementsByClassName('demand-reports-modal-pdf-viewer-outer')[0]
			.classList.remove('exist-demand-reports');
		setSelectedAddress('');
		selectedAddressNonState = '';
		setSelectedZip('');
		setSelectedTown('');
		selectedTownNonState = '';
		setSelectedState('');
		setSelectedCountry('');
		closeDemandReports();
	};

	const downloadPDF = () => {
		startDownload();
	};

	const [googleLimiter, setGoogleLimiter] = useState({});
	let googleReady = false;

	const findGooglePlaces = (evt: any) => {
		if (selectedAddress) {
			setSelectedAddress('');
			selectedAddressNonState = '';
		}
		const val = evt.currentTarget.value;
		if (val && typeof val === 'string' && googleReady) {
			setGoogleReady(false);
			setGoogleLimiter(
				setTimeout(() => {
					getGooglePlaces(val);
					googleReady = true;
				}, 1000)
			);
		} else if (val && typeof val === 'string' && !googleReady) {
			clearTimeout(googleLimiter);
			setGoogleLimiter(
				setTimeout(() => {
					getGooglePlaces(val);
					googleReady = true;
				}, 1000)
			);
		}
	};

	const selectAddress = address => {
		const newAdd = address.split(',');
		if (newAdd.length > 2) {
			newAdd.pop();
		}
		if (newAdd.length > 2) {
			newAdd.shift();
		}
		setSelectedAddress(newAdd[0] + newAdd[1].split(' ').join(', '));
		selectedAddressNonState = address;
		setSelectedZip(getZip(address));
		setSelectedTown(getTown(address));
		selectedTownNonState = getTown(address);
		setSelectedState(getState(address));
		setSelectedCountry(getCountry(address));

		const input = document.getElementsByClassName(
			'demand-reports-modal-location-input'
		)[0];
		if (input) {
			input.value = address;
		}
	};

	const getZip = address => {
		const arr0 = address.split(', USA');
		const arr1 = address.split(', United States');
		const arrCanada = address.split(', Canada');

		let zipStr;
		if (arr0.length > 1) {
			zipStr = arr0[0]
				.split('')
				.splice(arr0[0].split('').length - 5, 5)
				.join('');
		}
		if (arr1.length > 1) {
			zipStr = arr1[0]
				.split('')
				.splice(arr1[0].split('').length - 5, 5)
				.join('');
		}
		if (arrCanada.length > 1 && address.split(',').length > 2) {
			zipStr = arrCanada[0]
				.split('')
				.splice(arrCanada[0].split('').length - 7, 7)
				.join('')
				.split(' ')[0];
			if (zipStr.length !== 3) {
				zipStr = '';
			}
		}

		if (
			zipStr &&
			((typeof parseInt(zipStr) === 'number' &&
				!isNaN(parseInt(zipStr))) ||
				arrCanada.length > 1)
		) {
			return zipStr;
		}
		return '';
	};

	const getTown = address => {
		const isCanada = address.split('Canada').length > 1;
		if (!isCanada) {
			const hasAddress = (() => {
				const arr0 = address.split(',');
				const potentialZip = arr0[1]
					.slice(arr0[1].length - 5, arr0[1].length - 0)
					.trim();
				if (parseInt(potentialZip) / 1 === parseInt(potentialZip)) {
					return false;
				}

				return true;
			})();
			const arr0 = address.split(',');
			const name = hasAddress ? arr0[1].trim() : arr0[0].trim();
			if (name) {
				return name.trim();
			}
			return '';
		}
		const addArr = address.split(',');
		const str = addArr[addArr.length - 3];
		return str.trim();
	};

	const getCountry = address => {
		if (address.split('Canada').length > 1) {
			return 'Canada';
		}
		return 'United States';
	};

	const getState = (address: string) => {
		const arr0 = address.split(', USA');
		const arr1 = address.split(', United States');
		const arrCanada = address.split(', Canada');
		if (arr0.length > 1 || arr1.length > 1) {
			const regexUSA = address.match(
				/(A[LKSZRAEP]|C[AOT]|D[EC]|F[LM]|G[AU]|HI|I[ADLN]|K[SY]|LA|M[ADEHINOPST]|N[CDEHJMVY]|O[HKR]|P[ARW]|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY])/
			)[1];
			let regCleanUSA = regexUSA.split('"')[0];
			const DCreg = address.match(/(?<=\s)DC\b/);
			if (DCreg && DCreg[0] === 'DC') {
				regCleanUSA = 'DC';
			}
			let fullName;
			states.forEach(stateArr => {
				if (stateArr[1] == regCleanUSA.trim()) {
					fullName = stateArr[0];
				}
			});
			if (fullName) {
				return fullName;
			}
		} else {
			address = address.replace('Quebec', 'QC');
			address = address.replace('Prince Edward Island', 'PE');
			address = address.replace('Nova Scotia', 'NS');
			address = address.replace('New Brunswick', 'NB');
			address = address.replace('Newfoundland and Labrador', 'NL');
			address = address.replace('Ontario', 'ON');
			address = address.replace('Manitoba', 'MB');
			address = address.replace('Saskatchewan', 'SK');
			address = address.replace('Alberta', 'AB');
			address = address.replace('British Columbia', 'BC');
			address = address.replace('Yukon', 'YT');
			address = address.replace('Northwest Territories', 'NT');
			address = address.replace('Nunavut', 'NU');
			const regexCanada = address.match(
				/(A[B]|B[C]|M[B]|N[BFLTSU]|L[B]|O[N]|P[E]|Q[C]|S[K]|Y[T])/
			)[1];
			const regCleanCanada = regexCanada.split('"')[0];
			let fullName;
			provinces.forEach(stateArr => {
				if (stateArr[1] == regCleanCanada.trim()) {
					fullName = stateArr[0];
				}
			});
			if (fullName) {
				return fullName;
			}
		}
		if (fullName) {
			return fullName;
		}

		return '';
	};

	const hasZip = (address: string) => {
		const zip = getZip(address);

		if (zip) {
			return true;
		}

		return false;
	};

	const createPDFAddress = row => {
		setSelectedTown(row.address.city);
		selectedTownNonState = row.address.city;

		setSelectedAddress(
			`${row.address.city}, ${row.address.state}, ${row.address.zip}`
		);
		selectedAddressNonState = `${row.address.city}, ${row.address.state}, ${row.address.zip}`;
	};

	const styles = StyleSheet.create({
		page: {
			position: 'relative',
			backgroundColor: '#efeeee',
			paddingTop: '32px',
			paddingRight: '26px',
			paddingBottom: '32px',
			paddingLeft: '66px'
		},
		sideBanner: {
			position: 'absolute',
			top: '0px',
			left: '0px',
			width: '40px',
			height: '4040px',
			backgroundColor: '#020303'
		},
		contentSection: {},
		headerContainer: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start'
		},
		logo: {
			width: '48px',
			height: '48px'
		},
		titleHolder: {
			width: '200px',
			height: '48px',
			marginLeft: '16px'
		}
	});

	const getHeader = () => {
		return (
			<>
				<View style={styles.headerContainer}>
					<Image
						src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/637d58dfe27369364d59e522_big-logo-pdf.png"
						style={styles.logo}
					/>
					<View style={styles.titleHolder}>
						<View>
							<Text
								style={{
									fontSize: '24px',
									fontWeight: 'semibold',
									color: '#121212'
								}}
							>
								Demand Report
							</Text>
						</View>
						<View>
							<Text
								style={{
									color: '#1556ee',
									fontSize: '14px',
									fontWeight: 'semibold',
									paddingTop: '3px',
									width: '425px'
								}}
							>
								{selectedAddress || selectedAddressNonState}
							</Text>
						</View>
					</View>
				</View>
				<View style={{ position: 'flex', flexDirection: 'row' }}>
					<View
						style={{
							textAlign: 'center',
							width: '120px',
							height: '24px',
							backgroundColor: '#ffffff',
							color: '#050505',
							borderTopLeftRadius: '12px',
							borderTopRightRadius: '12px',
							borderBottomLeftRadius: '12px',
							borderBottomRightRadius: '12px',
							paddingTop: '5px',
							margin: '11px 0 0 33px'
						}}
					>
						<Text
							style={{
								fontSize: '12px',
								fontWeight: 'semibold',
								color: '#505050'
							}}
						>
							Requested Month
						</Text>
					</View>
					<View
						style={{
							textAlign: 'center',
							width: '120px',
							height: '24px',
							backgroundColor: '#f0ac00',
							color: '#050505',
							borderTopLeftRadius: '12px',
							borderTopRightRadius: '12px',
							borderBottomLeftRadius: '12px',
							borderBottomRightRadius: '12px',
							paddingTop: '5px',
							margin: '11px 0 0 16px'
						}}
					>
						<Text
							style={{
								color: '#ffffff',
								fontSize: '12px',
								fontWeight: 'semibold'
							}}
						>
							{currentMonth}, {currentYear}
						</Text>
					</View>
				</View>
				<View
					style={{
						position: 'flex',
						flexDirection: 'row',
						marginTop: '18px',
						width: '100%',
						height: '60px',
						borderTopLeftRadius: '12px',
						borderTopRightRadius: '12px',
						borderBottomLeftRadius: '12px',
						borderBottomRightRadius: '12px',
						backgroundColor: '#ffffff'
					}}
				>
					<Text
						style={{
							fontSize: '12px',
							fontWeight: 'normal',
							padding: '8px 21px',
							color: '#181818'
						}}
					>
						<Text>
							Consumer demand data is the number of local searches
							on Google and Google Maps for a business category in
							an area. For details on Resquared’s proprietary
							methodology, case studies, and recommended use
							cases, visit https://learn.re2.ai/demand-reports.
						</Text>
					</Text>
				</View>
			</>
		);
	};

	const getRow = (key, val) => {
		if (key[0].length < 13) {
			return (
				<View
					style={{
						position: 'flex',
						flexDirection: 'row',
						display: 'block',
						width: '100%',
						height: '25px',
						color: 'black'
					}}
				>
					<View
						style={{
							display: 'inlineBlock',
							width: '110px',
							height: '100%',
							zIndex: '999'
						}}
					>
						<Text
							style={{
								display: 'flex',
								flexDirection: 'row',
								flexWrap: 'wrap',
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%',
								textAlign: 'right',
								wordBreak: 'break-all'
							}}
						>
							{key[0]}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '35px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val?.value && Math.ceil(val.value)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val?.percentage && val.percentage.toFixed(1)} %
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{isNaN(val.percentageSV) ||
							val.percentageSV == 'null' ||
							!val.percentageSV ? (
								<Text
									style={{
										fontSize: '16px',
										color: '#F57474'
									}}
								>
									&nbsp;&#8211;
								</Text>
							) : (
								<>
									{val.percentageDiffSV > 0 ? (
										<Image
											src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736e821dc87023f908205_Vector%20(6).png"
											style={{
												display: 'block',
												width: '13px',
												height: '6px'
											}}
										/>
									) : (
										<Image
											src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736d5c53397744cec88e4_Vector%20(5).png"
											style={{
												position: 'absolute',
												display: 'block',
												width: '13px',
												height: '6px'
											}}
										/>
									)}

									{true && (
										<>
											{`${val.percentageDiffSV.toFixed(1)}%`}
										</>
									)}
								</>
							)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val.percentageDiffNational > 0 ? (
								<Image
									src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736e821dc87023f908205_Vector%20(6).png"
									style={{
										display: 'block',
										width: '13px',
										height: '6px'
									}}
								/>
							) : (
								<Image
									src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736d5c53397744cec88e4_Vector%20(5).png"
									style={{
										position: 'absolute',
										display: 'block',
										width: '13px',
										height: '6px'
									}}
								/>
							)}

							{true && (
								<>
									{`${val.percentageDiffNational.toFixed(1)}%`}
								</>
							)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val.businessCountItem}
						</Text>
					</View>
				</View>
			);
		}
		return (
			<>
				<View
					style={{
						position: 'flex',
						flexDirection: 'row',
						display: 'block',
						width: '100%',
						height: '25px',
						color: 'black'
					}}
				>
					<View
						style={{
							display: 'inlineBlock',
							width: '110px',
							height: '100%',
							zIndex: '999'
						}}
					>
						<Text
							style={{
								display: 'flex',
								flexDirection: 'row',
								flexWrap: 'wrap',
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%',
								textAlign: 'right',
								wordBreak: 'break-all'
							}}
						>
							{key[0].split(' ').length <= 1 ? (
								<>
									{key[0].slice(0, 10)}
									{key[0].slice(0, 10)[
										key[0].slice(0, 10).length - 1
									] === ' '
										? ''
										: '-'}
								</>
							) : (
								<>{key[0].split(' ')[0]}</>
							)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '35px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val?.value && Math.ceil(val.value)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val?.percentage && val.percentage.toFixed(1)}%
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{isNaN(val.percentageSV) ||
							val.percentageSV == 'null' ||
							!val.percentageSV ? (
								<Text
									style={{
										fontSize: '16px',
										color: '#F57474'
									}}
								>
									&nbsp;&#8211;
								</Text>
							) : (
								<>
									{val.percentageDiffSV > 0 ? (
										<Image
											src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736e821dc87023f908205_Vector%20(6).png"
											style={{
												display: 'block',
												width: '13px',
												height: '6px'
											}}
										/>
									) : (
										<Image
											src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736d5c53397744cec88e4_Vector%20(5).png"
											style={{
												position: 'absolute',
												display: 'block',
												width: '13px',
												height: '6px'
											}}
										/>
									)}

									{true && (
										<>
											{`${val.percentageDiffSV.toFixed(1)}%`}
										</>
									)}
								</>
							)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val.percentageDiffNational > 0 ? (
								<Image
									src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736e821dc87023f908205_Vector%20(6).png"
									style={{
										display: 'block',
										width: '13px',
										height: '6px'
									}}
								/>
							) : (
								<Image
									src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c736d5c53397744cec88e4_Vector%20(5).png"
									style={{
										position: 'absolute',
										display: 'block',
										width: '13px',
										height: '6px'
									}}
								/>
							)}

							{true && (
								<>
									{`${val.percentageDiffNational.toFixed(1)}%`}
								</>
							)}
						</Text>
					</View>
					<View
						style={{
							display: 'inlineBlock',
							width: '55px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%'
							}}
						>
							{val.businessCountItem}
						</Text>
					</View>
				</View>
				<View
					style={{
						position: 'flex',
						flexDirection: 'row',
						display: 'block',
						width: '100%',
						height: '25px',
						color: 'black'
					}}
				>
					<View
						style={{
							display: 'inlineBlock',
							width: '108px',
							height: '100%',
							zIndex: '999'
						}}
					>
						<Text
							style={{
								display: 'flex',
								flexDirection: 'row',
								flexWrap: 'wrap',
								position: 'relative',
								fontSize: '12px',
								fontWeight: 'normal',
								color: '#181818',
								width: '100%',
								height: '100%',
								marginTop: '-3px',
								textAlign: 'right',
								wordBreak: 'break-all'
							}}
						>
							{key[0].split(' ').length <= 1 ? (
								<>{key[0].slice(10, 1000)}</>
							) : (
								<>
									{key[0].split(' ').slice(1, 1000).join(' ')}
								</>
							)}
						</Text>
					</View>
				</View>
			</>
		);
	};

	const getSection1 = () => {
		return (
			<View
				style={{
					marginTop: '18px',
					width: '100%',
					height: '309px',
					padding: '16px',
					borderTopLeftRadius: '12px',
					borderTopRightRadius: '12px',
					borderBottomLeftRadius: '12px',
					borderBottomRightRadius: '12px',
					backgroundColor: '#ffffff'
				}}
			>
				<Text
					style={{
						position: 'relative',
						fontSize: '12px',
						fontWeight: 'normal',
						color: '#181818',
						width: '100%',
						height: '25px',
						borderBottom: '1px solid #b7b7b7'
					}}
				>
					Top Search Volumes in this neighborhood
				</Text>
				<Text
					style={{
						position: 'absolute',
						top: '21px',
						right: '24px',
						fontSize: '10px',
						fontWeight: 'normal',
						color: '#404040'
					}}
				>
					{(selectedTown || selectedTownNonState) && (
						<>
							Comparison city:&nbsp;
							<Text
								style={{
									display: 'inlineBlock',
									color: '#5066d7'
								}}
							>
								{selectedTown || selectedTownNonState}
							</Text>
						</>
					)}
				</Text>

				<View
					style={{
						display: 'flex',
						flexDirection: 'row',
						width: '100%',
						padding: '16px 0'
					}}
				>
					<View
						style={{
							width: '60px',
							height: '18px',
							marginLeft: '142px'
						}}
					>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'bold',
								color: '#f0ac00'
							}}
						>
							Number
						</Text>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							Of Searches
						</Text>
					</View>
					<View
						style={{
							width: '60px',
							height: '18px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'bold',
								color: '#f0ac00'
							}}
						>
							Local Search
						</Text>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							Percentage
						</Text>
					</View>

					<View
						style={{
							width: '60px',
							height: '18px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							City
						</Text>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							Average
						</Text>
					</View>

					<View
						style={{
							width: '60px',
							height: '18px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							National
						</Text>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							Average
						</Text>
					</View>

					<View
						style={{
							width: '60px',
							height: '18px',
							marginLeft: '15px'
						}}
					>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							Est. Number of
						</Text>
						<Text
							style={{
								fontSize: '8px',
								fontWeight: 'medium',
								color: '#404040'
							}}
						>
							Businesses
						</Text>
					</View>
				</View>

				{report?.length > 0 &&
					report.slice(0, 9).map(row => {
						const key = Object.keys(row);
						const val = row[key];
						return getRow(key, val);
					})}
			</View>
		);
	};

	const getBottomSection = () => {
		return (
			<View>
				<View
					style={{
						display: 'inlineBlock',
						width: '50%'
					}}
				>
					<View
						style={{
							position: 'relative',
							width: '244px',
							height: '56px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '14px',
							paddingRight: '9px',
							paddingBottom: '14px',
							paddingLeft: '32px',
							backgroundColor: '#ffffff'
						}}
					>
						<Image
							src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c887bbac4fd15ccc472f89_Search%20Volume.png"
							style={{
								position: 'absolute',
								width: '20px',
								top: '15px',
								left: '8px'
							}}
						/>
						<Text
							style={{
								width: '208px',
								fontSize: '11px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							In {currentMonth}, over{' '}
							<Text style={{ color: '#5066d7' }}>
								{report[7][
									Object.keys(report[7])
								].value.toFixed(0)}
							</Text>{' '}
							locals searched for{' '}
							<Text style={{ color: '#5066d7' }}>
								{Object.keys(report[7])}
							</Text>
						</Text>
					</View>
					<View
						style={{
							position: 'relative',
							width: '244px',
							height: '56px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '14px',
							paddingRight: '9px',
							paddingBottom: '14px',
							paddingLeft: '32px',
							backgroundColor: '#ffffff'
						}}
					>
						<Image
							src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c887bbac4fd15ccc472f89_Search%20Volume.png"
							style={{
								position: 'absolute',
								width: '20px',
								top: '15px',
								left: '8px'
							}}
						/>
						<Text
							style={{
								width: '208px',
								fontSize: '11px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							This month{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[4])}"
							</Text>{' '}
							had around{' '}
							<Text style={{ color: '#5066d7' }}>
								{report[4][
									Object.keys(report[4])
								].value.toFixed(0)}
							</Text>{' '}
							searches in this neighborhood.
						</Text>
					</View>
					<View
						style={{
							position: 'relative',
							width: '244px',
							height: '70px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '14px',
							paddingRight: '9px',
							paddingBottom: '14px',
							paddingLeft: '32px',
							backgroundColor: '#ffffff'
						}}
					>
						<Image
							src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c887bbac4fd15ccc472f89_Search%20Volume.png"
							style={{
								position: 'absolute',
								width: '20px',
								top: '15px',
								left: '8px'
							}}
						/>
						<Text
							style={{
								width: '200px',
								fontSize: '11px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							The category{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[2])}"
							</Text>{' '}
							has around{' '}
							<Text style={{ color: '#5066d7' }}>
								{report[7][
									Object.keys(report[7])
								].value.toFixed(0)}
							</Text>{' '}
							searches, more than both{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[11])}"
							</Text>{' '}
							and{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[16])}"
							</Text>
						</Text>
					</View>
					<View
						style={{
							position: 'relative',
							width: '244px',
							height: '70px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '14px',
							paddingRight: '9px',
							paddingBottom: '14px',
							paddingLeft: '32px',
							backgroundColor: '#ffffff'
						}}
					>
						<Image
							src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c887bbac4fd15ccc472f89_Search%20Volume.png"
							style={{
								position: 'absolute',
								width: '20px',
								top: '15px',
								left: '8px'
							}}
						/>
						<Text
							style={{
								width: '200px',
								fontSize: '11px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							This month,{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[0])}"
							</Text>
							,{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[1])}"
							</Text>
							, and{' '}
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[3])}"
							</Text>{' '}
							will make up more than
							<Text style={{ color: '#5066d7' }}>
								&nbsp;
								{parseInt(
									report[0][
										Object.keys(report[0])
									].percentage.toFixed(0)
								) +
									parseInt(
										report[1][
											Object.keys(report[1])
										].percentage.toFixed(0)
									) +
									parseInt(
										report[3][
											Object.keys(report[3])
										].percentage.toFixed(0)
									)}
								%&nbsp;
							</Text>
							of local searches
						</Text>
					</View>
				</View>
				<View
					style={{
						position: 'absolute',
						left: '50%',
						display: 'inlineBlock',
						paddingLeft: '4px',
						width: '50%'
					}}
				>
					<View
						style={{
							position: 'relative',
							display: 'inline-block',
							width: '244px',
							height: '145px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '13px',
							paddingRight: '16px',
							paddingBottom: '13px',
							paddingLeft: '16px',
							backgroundColor: '#ffffff'
						}}
					>
						<Text
							style={{
								width: '208px',
								fontSize: '14px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							Top Local Searches
						</Text>
						<View
							style={{
								display: 'inlineBlock',
								width: '105px',
								marginTop: '15px',
								height: '100px'
							}}
						>
							{topFive &&
								topFive.length &&
								topFive.map((item, index) => {
									let bgColor;
									if (index === 0) {
										bgColor = '#7385DF';
									}
									if (index === 1) {
										bgColor = '#F0AC00';
									}
									if (index === 2) {
										bgColor = '#404040';
									}
									if (index === 3) {
										bgColor = '#9CDBF1';
									}
									if (index === 4) {
										bgColor = '#DBDBDB';
									}
									return (
										<View
											style={{
												width: '105px',
												height: '20px',
												paddingTop: '4px'
											}}
										>
											<View
												style={{
													width: '12px',
													height: '8px',
													borderRadius: '3px',
													backgroundColor: bgColor
												}}
											/>
											<Text
												style={{
													position: 'absolute',
													left: '14px',
													top: '3px',
													fontSize: '10px',
													fontWeight: 'medium',
													lineHeight: '12px',
													color: '#181818'
												}}
											>
												{item[
													Object.keys(item)[0]
												].percentage.toFixed(1)}
												% {Object.keys(item)[0]}
											</Text>
										</View>
									);
								})}
						</View>
						<View
							style={{
								position: 'absolute',
								left: '124px',
								top: '42px',
								display: 'inlineBlock',
								width: '105px',
								height: '89px'
							}}
						>
							<View
								style={{
									position: 'flex',
									flexDirection: 'row'
								}}
							>
								<View
									style={{
										width: `${topFive[0][Object.keys(topFive[0])[0]].percentage}%`,
										height: '90px',
										backgroundColor: '#7385DF'
									}}
								/>
								<View
									style={{
										width: `${topFive[1][Object.keys(topFive[1])[0]].percentage}%`,
										height: '90px',
										backgroundColor: '#F0AC00'
									}}
								/>
								<View
									style={{
										width: `${topFive[2][Object.keys(topFive[2])[0]].percentage}%`,
										height: '90px',
										backgroundColor: '#404040'
									}}
								/>
								<View
									style={{
										width: `${topFive[3][Object.keys(topFive[3])[0]].percentage}%`,
										height: '90px',
										backgroundColor: '#9CDBF1'
									}}
								/>
								<View
									style={{
										width: `${topFive[4][Object.keys(topFive[4])[0]].percentage}%`,
										height: '90px',
										backgroundColor: '#DBDBDB'
									}}
								/>
							</View>
						</View>
					</View>
					<View
						style={{
							position: 'relative',
							width: '244px',
							height: '56px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '14px',
							paddingRight: '9px',
							paddingBottom: '14px',
							paddingLeft: '32px',
							backgroundColor: '#ffffff'
						}}
					>
						<Image
							src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c887bbac4fd15ccc472f89_Search%20Volume.png"
							style={{
								position: 'absolute',
								width: '20px',
								top: '15px',
								left: '8px'
							}}
						/>
						<Text
							style={{
								width: '208px',
								fontSize: '11px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							Local demand for
							<Text style={{ color: '#5066d7' }}>
								&nbsp;
								{Object.keys(
									report[
										nationBubbleInd ||
											nationBubbleIndNoState
									]
								)}
								&nbsp;
							</Text>
							is
							<Text style={{ color: '#5066d7' }}>
								&nbsp;
								{report[
									nationBubbleInd || nationBubbleIndNoState
								][
									Object.keys(
										report[
											nationBubbleInd ||
												nationBubbleIndNoState
										]
									)
								].percentageDiffNational.toFixed(1)}
								%&nbsp;
							</Text>
							higher than the rest of the country
						</Text>
					</View>
					<View
						style={{
							position: 'relative',
							width: '244px',
							height: '56px',
							borderRadius: '16px',
							marginTop: '8px',
							paddingTop: '14px',
							paddingRight: '9px',
							paddingBottom: '14px',
							paddingLeft: '32px',
							backgroundColor: '#ffffff'
						}}
					>
						<Image
							src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/63c887bbac4fd15ccc472f89_Search%20Volume.png"
							style={{
								position: 'absolute',
								width: '20px',
								top: '15px',
								left: '8px'
							}}
						/>
						<Text
							style={{
								width: '208px',
								fontSize: '11px',
								fontWeight: 'medium',
								color: '#181818'
							}}
						>
							<Text style={{ color: '#5066d7' }}>
								"{Object.keys(report[3])}"
							</Text>{' '}
							is
							<Text style={{ color: '#5066d7' }}>
								&nbsp;
								{report[2][Object.keys(report[2])]
									.percentageDiffSV &&
								(!isNaN(
									report[2][Object.keys(report[2])]
										.percentageDiffSV
								) ||
									report[2][Object.keys(report[2])]
										.percentageDiffSV != 'null')
									? report[2][
											Object.keys(report[2])
										].percentageDiffSV.toFixed(1)
									: report[2][
											Object.keys(report[2])
										].percentageDiffNational.toFixed(1)}
								%&nbsp;
							</Text>
							more searched in this zip code than the
							{report[2][Object.keys(report[2])]
								.percentageDiffSV &&
							(!isNaN(
								report[2][Object.keys(report[2])]
									.percentageDiffSV
							) ||
								report[2][Object.keys(report[2])]
									.percentageDiffSV != 'null')
								? ' surrounding city'
								: ' country'}
						</Text>
					</View>
				</View>
			</View>
		);
	};

	const getPageTwo = () => {
		return (
			<View style={{ height: '768px', marginTop: '10px' }}>
				<View style={styles.headerContainer}>
					<Image
						src="https://uploads-ssl.webflow.com/632342b27d7b5888cd224437/637d58dfe27369364d59e522_big-logo-pdf.png"
						style={styles.logo}
					/>
					<View style={styles.titleHolder}>
						<View>
							<Text
								style={{
									fontSize: '24px',
									fontWeight: 'semibold',
									color: '#121212'
								}}
							>
								Demand Report
							</Text>
						</View>
						<View>
							<Text
								style={{
									color: '#1556ee',
									fontSize: '14px',
									fontWeight: 'semibold',
									paddingTop: '3px',
									width: '425px'
								}}
							>
								{selectedAddress || selectedAddressNonState}
							</Text>
						</View>
					</View>
				</View>
				<View style={{ position: 'flex', flexDirection: 'row' }}>
					<View
						style={{
							textAlign: 'center',
							width: '120px',
							height: '24px',
							backgroundColor: '#ffffff',
							color: '#050505',
							borderTopLeftRadius: '12px',
							borderTopRightRadius: '12px',
							borderBottomLeftRadius: '12px',
							borderBottomRightRadius: '12px',
							paddingTop: '5px',
							margin: '11px 0 0 33px'
						}}
					>
						<Text
							style={{
								fontSize: '12px',
								fontWeight: 'semibold',
								color: '#505050'
							}}
						>
							Requested Month
						</Text>
					</View>
					<View
						style={{
							textAlign: 'center',
							width: '120px',
							height: '24px',
							backgroundColor: '#f0ac00',
							color: '#050505',
							borderTopLeftRadius: '12px',
							borderTopRightRadius: '12px',
							borderBottomLeftRadius: '12px',
							borderBottomRightRadius: '12px',
							paddingTop: '5px',
							margin: '11px 0 0 16px'
						}}
					>
						<Text
							style={{
								color: '#ffffff',
								fontSize: '12px',
								fontWeight: 'semibold'
							}}
						>
							{currentMonth}, {currentYear}
						</Text>
					</View>
				</View>
				<View
					style={{
						width: '100%',
						padding: '16px',
						borderRadius: '16px',
						marginTop: '16px',
						backgroundColor: '#ffffff'
					}}
				>
					<Text
						style={{
							fontSize: '16px',
							fontWeight: 'medium',
							color: '#404040',
							paddingBottom: '16px',
							borderBottom: '1px solid #b7b7b7'
						}}
					>
						Search Volumes - Specific Categories
					</Text>
					<View style={{ marginTop: '16px' }}>
						<View
							style={{
								display: 'flex',
								flexDirection: 'row',
								width: '100%',
								padding: '16px 0'
							}}
						>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '142px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Number
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Of Searches
								</Text>
							</View>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Local Search
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Percentage
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									City
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									National
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Businesses
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Count
								</Text>
							</View>
						</View>
						{report?.length > 0 &&
							report.slice(0, 15).map(row => {
								const key = Object.keys(row);
								const val = row[key];
								return getRow(key, val);
							})}
					</View>
				</View>
			</View>
		);
	};

	const getPageThree = () => {
		return (
			<View style={{ height: '780px', marginTop: '5px' }}>
				<View
					style={{
						width: '100%',
						padding: '16px',
						borderRadius: '16px',
						backgroundColor: '#ffffff'
					}}
				>
					<Text
						style={{
							fontSize: '16px',
							fontWeight: 'medium',
							color: '#404040',
							paddingBottom: '16px',
							borderBottom: '1px solid #b7b7b7'
						}}
					>
						Search Volumes - Specific Categories
					</Text>
					<View style={{ marginTop: '16px' }}>
						<View
							style={{
								display: 'flex',
								flexDirection: 'row',
								width: '100%',
								padding: '16px 0'
							}}
						>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '142px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Number
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Of Searches
								</Text>
							</View>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Local Search
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Percentage
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									City
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									National
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Businesses
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Count
								</Text>
							</View>
						</View>
						{report?.length > 0 &&
							report.slice(16, 30).map(row => {
								const key = Object.keys(row);
								const val = row[key];
								return getRow(key, val);
							})}
					</View>
				</View>
			</View>
		);
	};

	const getPageFour = () => {
		return (
			<View style={{ height: '780px' }}>
				<View
					style={{
						width: '100%',
						padding: '16px',
						borderRadius: '16px',
						backgroundColor: '#ffffff'
					}}
				>
					<Text
						style={{
							fontSize: '16px',
							fontWeight: 'medium',
							color: '#404040',
							paddingBottom: '16px',
							borderBottom: '1px solid #b7b7b7'
						}}
					>
						Search Volumes - Specific Categories
					</Text>
					<View style={{ marginTop: '16px' }}>
						<View
							style={{
								display: 'flex',
								flexDirection: 'row',
								width: '100%',
								padding: '16px 0'
							}}
						>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '142px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Number
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Of Searches
								</Text>
							</View>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Local Search
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Percentage
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									City
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									National
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Businesses
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Count
								</Text>
							</View>
						</View>

						{report?.length > 0 &&
							report.slice(31, 45).map(row => {
								const key = Object.keys(row);
								const val = row[key];
								return getRow(key, val);
							})}
					</View>
				</View>
			</View>
		);
	};

	const getPageFive = () => {
		return (
			<View style={{ height: '760px' }}>
				<View
					style={{
						width: '100%',
						padding: '16px',
						borderRadius: '16px',
						backgroundColor: '#ffffff'
					}}
				>
					<Text
						style={{
							fontSize: '16px',
							fontWeight: 'medium',
							color: '#404040',
							paddingBottom: '16px',
							borderBottom: '1px solid #b7b7b7'
						}}
					>
						Search Volumes - Specific Categories
					</Text>
					<View style={{ marginTop: '16px' }}>
						<View
							style={{
								display: 'flex',
								flexDirection: 'row',
								width: '100%',
								padding: '16px 0'
							}}
						>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '142px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Number
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Of Searches
								</Text>
							</View>
							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'bold',
										color: '#f0ac00'
									}}
								>
									Local Search
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Percentage
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									City
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									National
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Average
								</Text>
							</View>

							<View
								style={{
									width: '60px',
									height: '18px',
									marginLeft: '15px'
								}}
							>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Businesses
								</Text>
								<Text
									style={{
										fontSize: '8px',
										fontWeight: 'medium',
										color: '#404040'
									}}
								>
									Count
								</Text>
							</View>
						</View>

						{report?.length > 0 &&
							report.slice(46, 1000).map(row => {
								const key = Object.keys(row);
								const val = row[key];
								return getRow(key, val);
							})}
					</View>
				</View>
			</View>
		);
	};

	const DemandReportPDF = () => {
		return (
			<Document>
				<Page size="A4" style={styles.page}>
					<View style={styles.sideBanner} />
					<View style={styles.contentSection}>
						{getHeader()}

						{getSection1()}

						{getBottomSection()}

						{getPageTwo()}

						{getPageThree()}

						{getPageFour()}

						{getPageFive()}
					</View>
				</Page>
			</Document>
		);
	};

	const openDemandReports = () => {
		const modalEl = document.getElementsByClassName(
			'demand-reports-modal-outer'
		)[0];
		modalEl.classList.add('exist-demand-reports');
		setTimeout(() => {
			modalEl.classList.add('visible-demand-reports');
		}, 0);
	};

	const closeDemandReports = () => {
		const modalEl = document.getElementsByClassName(
			'demand-reports-modal-outer'
		)[0];
		const inputEl = modalEl.querySelector(
			'.demand-reports-modal-location-input'
		);
		modalEl.classList.remove('visible-demand-reports');
		clearGooglePlaces();
		setTimeout(() => {
			modalEl.classList.remove('exist-demand-reports');
			resetDemandReportsFailure();
			setSelectedZip('');
			setSelectedState('');
			setSelectedCountry('');
			if (inputEl) {
				inputEl.value = '';
			}
		}, 300);
		setShowEmptyState(false);
	};

	return (
		<>

		<div className={`demand-reports-holder ${user?.features?.platformRedesign ? 'demand-reports-holder-redesign' : ''}`}>
			
			<div className="demand-reports-modal-pdf-viewer-outer">
				<div className="demand-reports-modal-pdf-viewer" />
				<CloseThin
					className="demand-reports-modal-pdf-viewer-close-button"
					onClick={closePDFPreview}
				/>
			</div>
			<div className="demand-reports-modal-outer">
				<div
					className="demand-reports-modal-bg"
					onClick={closeDemandReports}
				/>
				<div className="demand-reports-modal-inner">
					{showSuccessModal && (
						<div className="demand-reports-modal-success-overlay">
							<div className="demand-reports-modal-success-header">
								Success!
							</div>
							<div className="demand-reports-modal-success-subheader">
								Your Demand Report is being built.
							</div>
							<div className="demand-reports-modal-success-text">
								It will be ready in about 10 - 15 minutes. We'll
								send you an email when it's ready. Please check
								your spam folder if you don't see it.
							</div>
						</div>
					)}
					<CloseThin
						className="demand-reports-modal-pdf-viewer-close-button"
						onClick={closeDemandReports}
					/>
					
					<div className="demand-reports-modal-content-section">
						<div className="demand-reports-modal-title">
							New Demand Report
						</div>
						<div className="demand-reports-modal-subtitle">
							Demand Reports uses Resquared's Demand algorithm to
							show you the approximate demand for different
							businesses in an area.
						</div>
						<div className="demand-reports-modal-quantity">
							You have{' '}
							{demandReportAllReports?.demandLimitLeft
								? demandReportAllReports.demandLimitLeft
								: '0'}{' '}
							reports remaining this month
						</div>
						<div className="demand-reports-modal-location-selector">
							<input
								type="text"
								ref={addressRef}
								placeholder="Type in a Street Address"
								className="demand-reports-modal-location-input"
								onKeyUp={evt => {
									resetDemandReportsFailure();
									if (evt.keyCode === 8) {
										setSelectedZip('');
										setSelectedState('');
										setSelectedCountry('');
									}
									if (evt.currentTarget.value.length > 2) {
										findGooglePlaces(evt);
									}
								}}
							/>
							<ChevronColorless className="demand-reports-modal-chevron-icon" />
						</div>
						{typingAddress || googlePlacesLoading ? (
							<div
								className={`demand-reports-search-dropdown-holder ${googlePlaces?.length > 1 ? 'demand-reports-search-dropdown-holder-scrollable' : ''}`}
							>
								<div className="demand-reports-search-dropdown-item demand-reports-search-dropdown-item-top">
									Suggested Locations
								</div>
								<div className="demand-reports-dropdown-loader-holder">
									<Loader
										className="demand-reports-dropdown-loader"
										status={Status.LOADING}
									/>
								</div>
							</div>
						) : (
							''
						)}
						{showEmptyState &&
						!typingAddress &&
						!googlePlacesLoading &&
						googlePlaces &&
						googlePlaces?.length < 1 ? (
							<div
								className={`demand-reports-search-dropdown-holder ${googlePlaces?.length > 1 ? 'demand-reports-search-dropdown-holder-scrollable' : ''}`}
							>
								<div className="demand-reports-search-dropdown-item demand-reports-search-dropdown-item-top">
									Suggested Locations
								</div>
								<div className="demand-reports-dropdown-loader-holder">
									<div className="demand-reports-search-dropdown-item empty">
										Sorry, we were unable to find results
										for your input.{' '}
									</div>
								</div>
							</div>
						) : (
							''
						)}
						<div
							className={`demand-reports-search-dropdown-holder ${googlePlaces?.length > 1 ? 'demand-reports-search-dropdown-holder-scrollable' : ''}`}
						>
							{googlePlaces &&
								googlePlaces.length > 0 &&
								!selectedAddress && (
									<div>
										<div className="demand-reports-search-dropdown-item demand-reports-search-dropdown-item-top">
											Suggested Locations
										</div>
										{googlePlaces.map(place => {
											return (
												<div
													className={`demand-reports-search-dropdown-item ${!hasZip(place.formatted_address) && 'disabled-address'}`}
													key={Math.random()}
													onClick={() => {
														if (
															hasZip(
																place.formatted_address
															)
														) {
															selectAddress(
																place.formatted_address
															);
														} else {
															alert(
																'Select an address with a city and zip code please.'
															);
														}
													}}
												>
													{place.formatted_address}
												</div>
											);
										})}
									</div>
								)}
						</div>
						{createDemandReportFailure && (
							<div className="create-report-error">
								Sorry, there was an error creating your report{' '}
							</div>
						)}
						{createDemandReportFailure ==
							'Demand reports limit reached' && (
							<div className="create-report-error-explanation">
								{' '}
								It looks like you've hit your limit on demand
								reports for this period{' '}
							</div>
						)}
						{createDemandReportFailure ==
							'Missing location code' && (
							<div className="create-report-error-explanation">
								{' '}
								We currently don't have data for this zip code.
								Please try a nearby zip code for more accurate
								results{' '}
							</div>
						)}
						{createDemandReportFailure ==
							'Invalid zipCode parameter' && (
							<div className="create-report-error-explanation">
								{' '}
								It looks like this address has an invalid zip
								code. Please try a nearby zip code{' '}
							</div>
						)}
						{createDemandReportFailure ==
							'Missing zipCode parameter' && (
							<div className="create-report-error-explanation">
								{' '}
								Please select an address with a zip code{' '}
							</div>
						)}
						{createDemandReportFailure ==
							'Missing city parameter' && (
							<div className="create-report-error-explanation">
								{' '}
								Please select an address with a city{' '}
							</div>
						)}
						{createDemandReportFailure ==
							"Can't get population" && (
							<div className="create-report-error-explanation">
								{' '}
								We currently don't have data for this zip code.
								Please try a nearby area for more accurate
								results{' '}
							</div>
						)}
						<div
							className={`demand-reports-modal-generate-button ${!selectedState || !selectedZip || createDemandReportFailure ? 'disabled-report-button' : ''}`}
							onClick={() => {
								if (selectedZip && selectedState) {
									startDownload();
								}
							}}
						>
							<span>Generate Demand Report</span>
							<DemandReportIcon className="demand-reports-button-icon" />
						</div>
					</div>
				</div>
			</div>
			<div className={`demand-reports-controls ${user?.features?.platformRedesign ? 'demand-reports-controls-redesign' : ''}`}>
				{user?.features?.platformRedesign ?
					<HeaderComponent
						icon={<DemandReportIcon className="demand-reports-icon" />}
						name="Demand Reports"
						description="Demand Reports uses Resquared's Demand algorithm to show you the approximate demand for different businesses in an area."
					/> :
					<div className="demand-reports-controls-left">
						<div className="demand-reports-page-title">
							Demand Reports
						</div>
						<div className="demand-reports-page-subtitle">
							Demand Reports uses Resquared's Demand algorithm to show
							you the approximate demand for different businesses in
							an area.
						</div>
					</div>
				}
				<div 
					className={`${user?.features?.platformRedesign ?
					'demand-reports-controls-right-redesign' : 'demand-reports-controls-right'}`}
				>
					<div
						className={`demand-reports-generate-button ${reportLimitLeft <= 0 ? 'disabled-report-button' : ''}`}
						onClick={() => {
							if (reportLimitLeft > 0) {
								openDemandReports();
								if (user && user.email) {
									sendAnalytics({
										type: 'track',
										userId: user.email,
										eventName:
											'User opened Demand reports modal',
										properties: {}
									});
								}
							}
						}}
					>
						<span>New Demand Report</span>
						{!user?.features?.platformRedesign &&
							<DemandReportIcon className="demand-reports-button-icon-classic" />
						}
					</div>

					<div className="demand-reports-remaining">
						You have{' '}
						{demandReportAllReports?.demandLimitLeft
							? demandReportAllReports.demandLimitLeft
							: '0'}{' '}
						reports remaining this month
					</div>
					<div className="demand-reports-search">
						<input
							placeholder="Search a location"
							onKeyUp={searchDemandReports}
						/>
						<SearchIcon className="demand-reports-search-icon" />
					</div>
				</div>
			</div>
			<div className="demand-reports-list-holder">
				<div className="demand-reports-list-title">
					Historic Reports
				</div>
				<div className="demand-reports-list-row">
					<div className="demand-reports-list-row-block demand-reports-list-row-block-1">
						<span>Date</span>
					</div>
					<div className="demand-reports-list-row-block demand-reports-list-row-block-2">
						<span>Zip Code</span>
					</div>
				</div>
				{demandReportsAll?.map && demandReportsAll.length > 0 && (
					<>
						{demandReportsAll.map(row => {
							return (
								<div
									className="demand-reports-list-row"
									key={Math.random()}
								>
									<div className="demand-reports-list-row-block demand-reports-list-row-block-1">
										<span>
											{row.name.split(',')[0]},{' '}
											{row.name.split(',')[1]}
										</span>
									</div>
									<div className="demand-reports-list-row-block demand-reports-list-row-block-2">
										<span>{row.name.split(',')[2]}</span>
									</div>
									<div className="demand-reports-list-row-block demand-reports-list-row-block-3">
										<span
											onClick={() => {
												createPDFAddress(row);
												currentMonth =
													row.name.split(' ')[0];
												currentYear = row.name
													.split(',')[1]
													.trim();
												report = modelReport(row.data);
												if (user && user.email) {
													sendAnalytics({
														type: 'track',
														userId: user.email,
														eventName:
															'User opened an existing demand report',
														properties: {}
													});
												}
												setTimeout(() => {
													renderPdf();
												}, 500);
											}}
										>
											Download
										</span>
									</div>
								</div>
							);
						})}
					</>
				)}
				{(demandReportsAll?.length && demandReportsAll.length === 0) ||
					(noReports && (
						<div className="empty-demand-reports-state">
							Make a demand report to get started
						</div>
					))}
			</div>
		</div>
		</>
	);
}
